import { css, cx } from '@emotion/css';
import { useState, useEffect } from 'react';
import type { HTMLAttributes } from 'react';
import { FiClock, FiAlertCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../shared/ui/button/button';
import { ProfileEventCard, type ProfileEventCardData } from '../profile-event-card/profile-event-card';

// Placeholder data - will be replaced with actual API call when waitlist API is available
const PLACEHOLDER_EVENTS: ProfileEventCardData[] = [
  // {
  //   id: '1',
  //   title: 'Festival de Jazz 2026',
  //   imageUrl: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop',
  //   location: 'Teatro Municipal',
  //   date: '15 de Nov. 2026',
  //   badge: {
  //     text: 'Na lista de espera',
  //     variant: 'waiting',
  //   },
  // },
  // {
  //   id: '2',
  //   title: 'Show de Rock Internacional',
  //   imageUrl: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400&h=300&fit=crop',
  //   location: 'Arena Cultural',
  //   date: '22 de Nov. 2026',
  //   badge: {
  //     text: 'Na lista de espera',
  //     variant: 'waiting',
  //   },
  // },
  // {
  //   id: '3',
  //   title: 'Concerto de Música Clássica',
  //   imageUrl: 'https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=400&h=300&fit=crop',
  //   location: 'Sala São Paulo',
  //   date: '5 de Dez. 2026',
  //   badge: {
  //     text: 'Na lista de espera',
  //     variant: 'waiting',
  //   },
  // },
];

// Styles
const container = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  padding: 'var(--spacing-lg)',
  boxShadow: 'var(--shadow-200)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-md)',
  },
  '@media (max-width: 360px)': {
    padding: 'var(--spacing-sm)',
  },
});

const header = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-lg)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--spacing-sm)',
  },
});

const title = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 0,
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  },
});

const eventsList = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 'var(--spacing-md)',
  '@media (max-width: 96.8rem)': {
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  },
  '@media (max-width: 480px)': {
    gridTemplateColumns: '1fr',
  },
});

const viewAllContainer = css({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 'var(--spacing-lg)',
});

const viewAllButton = css({
  backgroundColor: 'transparent',
  border: 'none',
  color: 'var(--pc-600)',
  fontSize: 'var(--fs-350)',
  fontWeight: 500,
  cursor: 'pointer',
  padding: 'var(--spacing-xs)',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: 'var(--pc-700)',
    textDecoration: 'underline',
  },
});

const loadingState = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 'var(--spacing-2xl)',
  color: 'var(--g-600)',
  fontSize: 'var(--fs-400)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-xl)',
    fontSize: 'var(--fs-350)',
  },
});

const errorState = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--spacing-md)',
  padding: 'var(--spacing-lg)',
  backgroundColor: 'rgba(245, 101, 101, 0.1)',
  borderRadius: 'var(--spacing-xs)',
  border: '1px solid rgba(245, 101, 101, 0.3)',
  color: 'rgb(155, 44, 44)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-md)',
  },
});

const errorIcon = css({
  fontSize: '32px',
  '@media (max-width: 480px)': {
    fontSize: '28px',
  },
});

const emptyState = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--spacing-2xl) var(--spacing-xl)',
  gap: 'var(--spacing-md)',
  color: 'var(--g-600)',
  textAlign: 'center',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-xl) var(--spacing-md)',
  },
});

const emptyIcon = css({
  fontSize: '48px',
  color: 'var(--g-400)',
  '@media (max-width: 480px)': {
    fontSize: '40px',
  },
});

const emptyText = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-600)',
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-350)',
  },
});

export interface ProfileSubscribedEventsListProps
  extends HTMLAttributes<HTMLDivElement> {
  limit?: number;
  showTitle?: boolean;
  onEventClick?: (eventId: string) => void;
  onViewAllClick?: () => void;
}

export function ProfileSubscribedEventsList({
  limit,
  showTitle = true,
  onEventClick,
  onViewAllClick,
  className,
  style,
  ...props
}: ProfileSubscribedEventsListProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<ProfileEventCardData[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSubscribedEvents();
  }, [limit]);

  const fetchSubscribedEvents = async () => {
    setLoading(true);
    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // TODO: Replace with actual API call when waitlist API is available
      // const response = await getSubscribedEvents();
      // const eventsData = response.data;

      // Using placeholder data for now
      let eventsData = [...PLACEHOLDER_EVENTS];

      // Apply limit if specified
      if (limit && eventsData.length > limit) {
        eventsData = eventsData.slice(0, limit);
      }

      setEvents(eventsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching subscribed events:', err);
      setError('Não foi possível carregar os eventos da lista de espera');
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (eventId: string) => {
    if (onEventClick) {
      onEventClick(eventId);
    } else {
      navigate(`/evento/${eventId}`);
    }
  };

  const handleViewAllClick = () => {
    if (onViewAllClick) {
      onViewAllClick();
    } else {
      navigate('/perfil/lista-espera');
    }
  };

  if (loading) {
    return (
      <div className={cx(container, className)} style={style} {...props}>
        {showTitle && (
          <div className={header}>
            <h2 className={title}>
              <FiClock />
              Listas de Espera
            </h2>
          </div>
        )}
        <div className={loadingState}>
          <p>Carregando eventos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cx(container, className)} style={style} {...props}>
        {showTitle && (
          <div className={header}>
            <h2 className={title}>
              <FiClock />
              Listas de Espera
            </h2>
          </div>
        )}
        <div className={errorState}>
          <FiAlertCircle className={errorIcon} />
          <p>{error}</p>
          <Button onClick={fetchSubscribedEvents}>Tentar novamente</Button>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className={cx(container, className)} style={style} {...props}>
        {showTitle && (
          <div className={header}>
            <h2 className={title}>
              <FiClock />
              Listas de Espera
            </h2>
          </div>
        )}
        <div className={emptyState}>
          <FiClock className={emptyIcon} />
          <p className={emptyText}>
            Você ainda não está em nenhuma lista de espera.
          </p>
          <Button onClick={() => navigate('/')}>Explorar eventos</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cx(container, className)} style={style} {...props}>
      {showTitle && (
        <div className={header}>
          <h2 className={title}>
            <FiClock />
            {limit ? 'Listas de Espera Recentes' : 'Listas de Espera'}
          </h2>
        </div>
      )}

      <div className={eventsList}>
        {events.map((event) => (
          <ProfileEventCard
            key={event.id}
            event={event}
            onEventClick={handleEventClick}
          />
        ))}
      </div>

      {limit && events.length > 0 && (
        <div className={viewAllContainer}>
          <button className={viewAllButton} onClick={handleViewAllClick}>
            Ver todas as listas de espera
          </button>
        </div>
      )}
    </div>
  );
}

export default ProfileSubscribedEventsList;
